import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractSocialAdAlert } from "@similarweb/pro-api-client";
import { formatDate, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { ChannelIcon } from "../../../../common/ChannelIcon";
import { ContentRow } from "../../../../common/modal/details-rows/ContentRow";
import { DetailsRow } from "../../../../common/modal/details-rows/DetailsRow";
import { NaValue } from "../../../../common/modal/details-rows/NaValue";
import { NewTabLinkRow } from "../../../../common/modal/details-rows/NewTabLinkRow";
import { getPlatformName } from "../../../utils/getPlatformName";
import { DurationRow } from "../details-rows/DurationRow";
import { TypeRow } from "../details-rows/TypeRow";

import { StatusRow } from "./StatusRow";

type SocialAdDetailsSectionProps = {
    ad: ActivationAlertsContractSocialAdAlert;
};

export const SocialAdDetailsSection = ({ ad }: SocialAdDetailsSectionProps) => {
    const { translate } = useTranslate();

    return (
        <Stack
            gap="16px"
            minWidth={0}
            flex={1}
            data-automation="social-ad-details-section"
        >
            <Text
                fontVariant="h5_2"
                color={colorsPalettes.carbon[500]}
            >
                {translate("activation.alerts.cards.display-ads.modal.ad-details")}
            </Text>
            <DetailsRow label={translate("activation.alerts.modal.social-ads.ad-title")}>
                {ad.title ? (
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {ad.title}
                    </Text>
                ) : (
                    <NaValue />
                )}
            </DetailsRow>
            <ContentRow content={ad.body} />
            <TypeRow type="Image" />
            <StatusRow active={ad.active} />
            <DurationRow activeDays={ad.activeDays} />
            <DetailsRow label={translate("activation.alerts.modal.social-ads.first-seen")}>
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[500]}
                >
                    {formatDate(ad.firstSeen, undefined, shortMonthDayAndYear)}
                </Text>
            </DetailsRow>
            <DetailsRow label={translate("activation.alerts.modal.social-ads.platforms")}>
                {ad.platforms?.length ? (
                    <Flex
                        gap="12px"
                        flexWrap="wrap"
                    >
                        {ad.platforms.map((platform) => (
                            <Flex
                                key={platform}
                                gap="4px"
                                alignItems="center"
                            >
                                <ChannelIcon
                                    channel={platform}
                                    size={16}
                                />
                                <Text
                                    fontVariant="input2"
                                    color={colorsPalettes.carbon[500]}
                                >
                                    {getPlatformName(platform)}
                                </Text>
                            </Flex>
                        ))}
                    </Flex>
                ) : (
                    <NaValue />
                )}
            </DetailsRow>
            <NewTabLinkRow
                label={translate("activation.alerts.cards.display-ads.modal.landing-page")}
                url={ad.landingPage}
            />
        </Stack>
    );
};
